import { useEffect, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import ReactLoading from "react-loading";

import ExcelTable from "../components/Table";
import { useFile } from "../Contexts";

// ── Constants ────────────────────────────────────────────────────────────────

const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// ── Styled Components ────────────────────────────────────────────────────────

const ModelContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  padding: 12px;
  width: ${({ $isSliderOpen }) =>
    $isSliderOpen ? "calc(100vw - 241px)" : "calc(100vw - 130px)"};

  > div {
    text-decoration: underline;
  }
`;

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

async function parseExcelFile(file) {
  const ab = await file.arrayBuffer();
  const workbook = XLSX.read(ab, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    blankrows: false,
    defval: "",
  });

  const headers = (rows[0] ?? []).map((h) => (h === null ? "" : String(h)));
  const data = rows.slice(1);
  return { headers, data };
}

function buildWorkbook(headers, rows) {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  return wb;
}

function toBlob(workbook) {
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  return new Blob([buffer], { type: EXCEL_MIME });
}

function editedFileName(original) {
  return original
    ? original.replace(/(\.xlsx|\.xls)$/i, "_edited.xlsx")
    : "edited_data.xlsx";
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Model({ selectedFile, setCurrentSelect }) {
  const [headers, setHeaders] = useState([]);
  const [data, setData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [tbData, setTbData] = useState(null);

  const { setFiles, setSelectedFile, open } = useFile();

  // Parse file whenever selection changes
  useEffect(() => {
    if (!selectedFile) {
      setHeaders([]);
      setData([]);
      setFileName("");
      return;
    }

    setFileName(selectedFile.name);

    (async () => {
      try {
        const parsed = await parseExcelFile(selectedFile);
        setHeaders(parsed.headers);
        setData(parsed.data);
      } catch (err) {
        console.error("Failed to parse Excel file:", err);
        setHeaders([]);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [selectedFile]);

  // Persist a saved File object into app state
  const persistFile = (file) => {
    setFiles((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const idx = list.findIndex((f) => f.file.name === file.name);
      if (idx !== -1) {
        const updated = [...list];
        updated[idx] = { file };
        return updated;
      }
      return [...list, { file }];
    });

    if (typeof setSelectedFile === "function") {
      setCurrentSelect(file);
    }
  };

  const handleSaveButton = async () => {
    if (!headers.length) return console.warn("No headers to save.");

    const rows = Array.isArray(tbData) && tbData.length ? tbData : data;
    if (!rows?.length) return console.warn("No table rows to save.");

    try {
      const blob = toBlob(buildWorkbook(headers, rows));
      const name = editedFileName(fileName);
      const now = Date.now();

      // Modern File System Access API
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: name,
          types: [
            {
              description: "Excel Workbook",
              accept: { [EXCEL_MIME]: [".xlsx"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();

        persistFile(
          new File([blob], handle.name || name, {
            type: EXCEL_MIME,
            lastModified: now,
          }),
        );
        return;
      }

      // Fallback: trigger download then optionally add to app state
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), {
        href: url,
        download: name,
      }).click();
      URL.revokeObjectURL(url);

      if (window.confirm("Add the downloaded file to the app?")) {
        persistFile(
          new File([blob], name, { type: EXCEL_MIME, lastModified: now }),
        );
      }
    } catch (err) {
      console.error("Error saving Excel file:", err);
    }
  };

  return (
    <ModelContainer $isSliderOpen={open}>
      {isLoading && selectedFile ? (
        <Loading>
          <ReactLoading type="balls" color="#000" />
        </Loading>
      ) : (
        <ExcelTable
          data={data}
          headers={headers}
          fileName={fileName}
          tbData={tbData}
          setTbData={setTbData}
          handleSaveButton={handleSaveButton}
        />
      )}
    </ModelContainer>
  );
}
